import {
  type DefaultError,
  type DefaultedQueryObserverOptions,
  type MutationOptions,
  type QueryKey,
  type QueryObserverOptions,
} from "@-/com.npmjs.tanstack__react-query";
import onetime from "@-/com.npmjs.onetime";
import { type Deadline, unboundedWait, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { MemoryBoundedQueryClient } from "#memory-bounded-query-client";

/**
 * The backstop for every query function. Local reads settle in milliseconds and network reads in
 * seconds; this only has to be finite. Screens surface slowness much earlier on their own
 * (see the shared Suspense fallback); this bound guarantees the awaited promise ENDS, so every
 * Suspense boundary, `fetchQuery` awaiter and deduplicated waiter on the same key is released
 * into an error with a retry instead of waiting forever.
 */
export const QUERY_DEADLINE_MS = 30_000;
/** Mutations may do more work per call (a batch write, an upload) but are bounded all the same. */
export const MUTATION_DEADLINE_MS = 60_000;

/** Functions already wrapped, so re-defaulting an options object never nests deadlines. */
const getBoundedFunctions = onetime(() => new WeakSet<object>());

/** `meta.deadline` overrides the default for one query or mutation. */
function deadlineFromMeta(meta: Record<string, unknown> | undefined, fallback: number): Deadline {
  const deadline = meta?.deadline;
  if (typeof deadline === "number") {
    return deadline;
  }
  if (
    typeof deadline === "object" &&
    deadline !== null &&
    Reflect.get(deadline, "unbounded") === true
  ) {
    return unboundedWait(String(Reflect.get(deadline, "reason")));
  }
  return fallback;
}

function describeKey(key: QueryKey | undefined): string {
  if (key === undefined) {
    return "(no key)";
  }
  const [head, ...rest] = key;
  return rest.length === 0 ? String(head) : `${String(head)}(+${rest.length})`;
}

function bound<TFunction extends (...args: never[]) => unknown>(
  fn: TFunction,
  operation: string,
  deadline: Deadline,
): TFunction {
  if (getBoundedFunctions().has(fn)) {
    return fn;
  }
  /* eslint-disable typescript/no-unsafe-type-assertion -- same parameters; the result is the same promise, bounded */
  const bounded = ((...args: Parameters<TFunction>) =>
    withDeadline({ deadline, operation }, async () => fn(...args))) as unknown as TFunction;
  /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */
  getBoundedFunctions().add(bounded);
  return bounded;
}

/**
 * Every query and mutation this client runs is bounded — enforced here, where TanStack defaults
 * its options, so no call site can forget. A query function that never settles (a read gate that
 * is never released, a native call that never answers, a lock held forever, a worker that died)
 * otherwise parks its Suspense boundary on the fallback forever, and `fetchQuery` adopts the
 * in-flight promise, so every later awaiter of the same key joins it.
 *
 * The wrapper passes the query context through untouched (TanStack decides abortability by
 * whether the query function READS `signal`).
 */
export class DeadlineBoundedQueryClient extends MemoryBoundedQueryClient {
  override defaultQueryOptions<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = never,
  >(
    options:
      | QueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey, TPageParam>
      | DefaultedQueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey>,
  ): DefaultedQueryObserverOptions<TQueryFnData, TError, TData, TQueryData, TQueryKey> {
    const defaulted = super.defaultQueryOptions(options);
    const { queryFn } = defaulted;
    // `skipToken` is a symbol, so the function check also leaves a skipped query alone.
    if (typeof queryFn === "function") {
      defaulted.queryFn = bound(
        queryFn,
        `query ${describeKey(defaulted.queryKey)}`,
        deadlineFromMeta(defaulted.meta, QUERY_DEADLINE_MS),
      );
    }
    return defaulted;
  }

  // oxlint-disable-next-line typescript/no-explicit-any -- must match QueryClient's own signature
  override defaultMutationOptions<T extends MutationOptions<any, any, any, any>>(options?: T): T {
    const defaulted = super.defaultMutationOptions(options);
    const { mutationFn } = defaulted;
    if (typeof mutationFn === "function") {
      defaulted.mutationFn = bound(
        mutationFn,
        `mutation ${describeKey(defaulted.mutationKey)}`,
        deadlineFromMeta(defaulted.meta, MUTATION_DEADLINE_MS),
      );
    }
    return defaulted;
  }
}
